import React, { useState, useEffect, useRef } from "react";
import LoginModal from "../components/LoginModal";
import { useNavigate, useLocation } from "react-router-dom"; // ✅ Added useLocation
import { useAuth } from "../contexts/AuthContext";
import HowToUse from "./HowToUse";
import PrivacyPolicy from "./PrivacyPolicy"; // Import Privacy
import TermsOfUse from "./TermsOfUse"; // Import Terms
import Footer from "../components/Footer";
import "../App.css";
import landingLogo from "../assets/logo.png";

import AntigravityParticles from "../components/AntigravityParticles";

const LandingPage = () => {
  const { currentUser, loading } = useAuth(); // ✅ Get Auth State
  const navigate = useNavigate();
  const location = useLocation(); // ✅ Get Location State
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [currentPage, setCurrentPage] = useState("home");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Handle incoming navigation from other pages (e.g. Pricing Page)
  useEffect(() => {
    if (location.state && location.state.target) {
      const target = location.state.target;
      // Clear state so it doesn't run again on refresh
      window.history.replaceState({}, document.title);

      // Allow a small tick for mounting
      setTimeout(() => {
        handleNavClick(target);
      }, 100);
    }
  }, [location]);

  useEffect(() => {
    if (!loading && currentUser) {
      console.log("🚀 User already logged in, redirecting to /mainPage");
      navigate("/mainPage");
    }
  }, [currentUser, loading, navigate]);
  if (loading) {
    return null;
  }
  const handleNavClick = (target) => {
    setIsMobileMenuOpen(false);
    // Scroll to top immediately when switching to a full page view
    if (["how-to", "privacy", "terms"].includes(target)) {
      setCurrentPage(target);
      window.scrollTo(0, 0);
    } else {
      // If we are on a different page, go home first, then scroll
      if (currentPage !== "home") {
        setCurrentPage("home");
        // Wait for render to find the element
        setTimeout(() => {
          const element = document.getElementById(target);
          if (element) element.scrollIntoView({ behavior: "smooth" });
        }, 50);
      } else {
        // We are already home, just scroll
        const element = document.getElementById(target);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  // Helper to render current content
  const renderContent = () => {
    switch (currentPage) {
      case "how-to":
        return <HowToUse />;
      case "privacy":
        return <PrivacyPolicy onBack={() => handleNavClick("home")} />;
      case "terms":
        return <TermsOfUse onBack={() => handleNavClick("home")} />;
      default:
        return (
          <>
            {/* HOME PAGE CONTENT */}
            <section className="hero-section" id="home">
              <AntigravityParticles />
              <div className="hero-content">
                <div className="hero-badge">
                  <span>✨ AI-Native Clarity Engine</span>
                </div>
                <h1 className="hero-title">
                  See Your Startup <span className="highlight">Clearly</span>.
                  <br />
                  Build What Actually Works.
                </h1>
                <p className="hero-description">
                  MoonshotMap is your AI-native clarity engine that transforms
                  raw ideas, scattered notes, and pitch drafts into
                  crystal-clear strategic maps. Get your Clarity Score™, Fog
                  Index™, Reality Check Score™, and a full roadmap — built for
                  founders, operators and investors.
                </p>
                <div className="hero-buttons">
                  <button
                    className="cta-primary"
                    onClick={() => setShowLoginModal(true)}
                  >
                    Get Started Free
                  </button>
                  <button
                    className="cta-secondary"
                    onClick={() => setShowLoginModal(true)}
                  >
                    See Sample Maps
                  </button>
                </div>
                <div className="hero-stats">
                  <div className="stat">
                    <span className="stat-number">10,000+</span>
                    <span className="stat-label">Maps Created</span>
                  </div>
                  <div className="stat">
                    <span className="stat-number">Loved by</span>
                    <span className="stat-label">
                      Founders, Operators & Early-Stage Investors
                    </span>
                  </div>
                </div>
              </div>
            </section>

            {/* ENGINE SECTION */}
            <section
              className="features-section"
              id="engine"
              style={{ background: "var(--bg-secondary)" }}
            >
              <div className="section-header">
                <h2>The Moonshot Engine</h2>
                <p>Your mind. Our frameworks. A map you can act on.</p>
                <p
                  style={{
                    fontSize: "0.95rem",
                    marginTop: "1rem",
                    color: "var(--text-secondary)",
                  }}
                >
                  MoonshotMap analyzes your inputs — goals, notes, drafts, decks
                  — and produces:
                </p>
              </div>
              <div className="features-grid">
                {/* Card 1 */}
                <div className="feature-card">
                  <div className="feature-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 6v6l4 2" />
                    </svg>
                  </div>
                  <h3>Clarity Score™</h3>
                  <p>
                    Measures how well-defined your idea, model, and narrative
                    are.
                  </p>
                </div>
                {/* Card 2 */}
                <div className="feature-card">
                  <div className="feature-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M3 15h18" />
                      <path d="M5 10h14" />
                      <path d="M7 5h10" />
                    </svg>
                  </div>
                  <h3>Fog Index™</h3>
                  <p>
                    Surfaces ambiguity, contradictions, risky assumptions, and
                    missing proof points.
                  </p>
                </div>
                {/* Card 3 */}
                <div className="feature-card">
                  <div className="feature-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  </div>
                  <h3>Reality Check Score™</h3>
                  <p>
                    Evaluates feasibility, traction readiness, and investor
                    alignment.
                  </p>
                </div>
                {/* Card 4 - Interactive Map */}
                <div className="feature-card">
                  <div className="feature-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <polygon points="12 2 2 7 12 12 22 7 12 2" />
                      <polyline points="2 17 12 22 22 17" />
                      <polyline points="2 12 12 17 22 12" />
                    </svg>
                  </div>
                  <h3>Your Interactive Moonshot Map</h3>
                  <p>
                    A living, adaptive roadmap covering TAM/SAM/SOM, business
                    model, growth levers, risks, milestones, investor fit, and
                    execution steps. Everything is editable. Everything evolves
                    as you evolve.
                  </p>
                </div>
              </div>
            </section>

            {/* FOUNDERS SECTION (Who It's For) */}
            <section
              className="elevate-section"
              id="who-for"
              style={{ background: "white" }}
            >
              <div className="section-header">
                <h2>Who It’s For</h2>
                <p>Founders at any stage. Investors who need better signals.</p>
              </div>
              <div className="elevate-grid">
                <div className="elevate-item">
                  <div className="elevate-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  </div>
                  <h4>Idea Stage</h4>
                  <p>Test if your idea is worth pursuing.</p>
                </div>
                <div className="elevate-item">
                  <div className="elevate-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                      <line x1="8" y1="21" x2="16" y2="21" />
                      <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                  </div>
                  <h4>Pre-Seed / Seed</h4>
                  <p>
                    Build a crisp strategy and pitch investors with confidence.
                  </p>
                </div>
                <div className="elevate-item">
                  <div className="elevate-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                    </svg>
                  </div>
                  <h4>Series A Prep</h4>
                  <p>
                    Sharpen narratives, tighten assumptions, and scale with
                    clarity.
                  </p>
                </div>
                <div className="elevate-item">
                  <div className="elevate-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M16 12l-4-4-4 4" />
                    </svg>
                  </div>
                  <h4>Investors / Accelerators</h4>
                  <p>Standardize founder evaluation with Clarity Scores™.</p>
                </div>
              </div>
              <div
                style={{
                  textAlign: "center",
                  marginTop: "3rem",
                  color: "var(--text-secondary)",
                  fontWeight: "500",
                }}
              >
                MoonshotMap removes the guesswork so founders can move faster —
                and investors can make decisions with depth.
              </div>
            </section>

            {/* WHY IT MATTERS (NEW SECTION) */}
            <section style={{ background: "var(--bg-secondary)" }}>
              <div className="section-header">
                <h2>Why It Matters Today</h2>
                <p>
                  Startups move too fast for static plans. You need a living
                  map.
                </p>
              </div>

              <div className="why-container">
                <div
                  style={{
                    textAlign: "center",
                    maxWidth: "700px",
                    margin: "0 auto",
                  }}
                >
                  <p
                    style={{
                      fontSize: "1.2rem",
                      color: "var(--text-primary)",
                      marginBottom: "1rem",
                    }}
                  >
                    Markets shift. AI changes everything. New competitors emerge
                    overnight.
                  </p>
                  <p style={{ color: "var(--text-secondary)" }}>
                    MoonshotMap gives founders:
                  </p>
                </div>

                <div className="why-list">
                  <div className="why-item">
                    <div className="why-check">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <p>Adaptability instead of static decks</p>
                  </div>
                  <div className="why-item">
                    <div className="why-check">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <p>Benchmark intelligence powered by real startup data</p>
                  </div>
                  <div className="why-item">
                    <div className="why-check">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <p>Truth signals instead of guesswork</p>
                  </div>
                  <div className="why-item">
                    <div className="why-check">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <p>A clarity engine that compounds as your company grows</p>
                  </div>
                  <div className="why-item">
                    <div className="why-check">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <p>Decision clarity instead of noise</p>
                  </div>
                  <div className="why-item">
                    <div className="why-check">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <p>This is the future of founder intelligence.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ SECTION */}
            <section className="faq-section" id="faq">
              <div className="section-header">
                <h2>Frequently Asked Questions</h2>
                <p>Common questions about clarity, privacy, and pricing.</p>
              </div>
              <div className="faq-container">
                <div className="faq-grid">
                  {/* Q1 */}
                  <div className="faq-card">
                    <h4>Do you offer annual pricing?</h4>
                    <p>Yes. You get <strong>2 months free</strong> when you choose the annual plan.</p>
                  </div>
                  {/* Q2 */}
                  <div className="faq-card">
                    <h4>Can I cancel anytime?</h4>
                    <p>Yes, absolutely. There are no lock-in contracts for monthly plans.</p>
                  </div>
                  {/* Q3 */}
                  <div className="faq-card">
                    <h4>Do you store my idea?</h4>
                    <p>You control your data. You can delete your maps anytime. We prioritize your privacy (see Security).</p>
                  </div>
                  {/* Q4 */}
                  <div className="faq-card">
                    <h4>Is this a project management tool?</h4>
                    <p>No. It’s a <strong>clarity + mapping system</strong> designed to feed your strategy into execution tools.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* CONTACT SECTION */}
            <section
              className="contact-section"
              id="contact"
              style={{ background: "var(--bg-primary)" }}
            >
              <div className="section-header">
                <h2>Contact Us</h2>
                <p>
                  Have questions, feedback, partnership ideas, or need support?
                  We’d love to hear from you.
                </p>
              </div>

              <div className="contact-grid">
                {/* Box 1: General */}
                <div className="contact-card">
                  <div className="contact-icon">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </div>
                  <h3>Get in Touch</h3>
                  <p>
                    For general inquiries, support, or feedback on the platform.
                  </p>
                  <a href="mailto:hello@moonshotmap.com" className="email-link">
                    hello@moonshotmap.com
                  </a>
                  <p
                    style={{
                      fontSize: "0.8rem",
                      marginTop: "1rem",
                      marginBottom: 0,
                      color: "var(--text-secondary)",
                    }}
                  >
                    Typical response: 24–48 hours
                  </p>
                </div>

                {/* Box 2: Partnerships */}
                <div className="contact-card">
                  <div className="contact-icon">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                      <line x1="9" y1="9" x2="9.01" y2="9" />
                      <line x1="15" y1="9" x2="15.01" y2="9" />
                    </svg>
                  </div>
                  <h3>Partnerships</h3>
                  <p>
                    Looking to integrate MoonshotMap into your accelerator or
                    startup program?
                  </p>
                  <a href="mailto:hello@moonshotmap.com" className="email-link">
                    hello@moonshotmap.com
                  </a>
                </div>

                {/* Box 3: Press */}
                <div className="contact-card">
                  <div className="contact-icon">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  </div>
                  <h3>Press & Media</h3>
                  <p>For media inquiries, interviews, or brand assets.</p>
                  <a href="mailto:hello@moonshotmap.com" className="email-link">
                    hello@moonshotmap.com
                  </a>
                </div>
              </div>

              <div className="contact-footer-note">
                We’re here to help you get clarity — wherever you are in your
                journey.
              </div>
            </section>
          </>
        );
    }
  };

  return (
    <div className="landing-container">
      {/* Navigation */}
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-content">
          <a onClick={() => handleNavClick("home")} className="logo-brand">
            <img src={landingLogo} alt="Moonshot Logo" className="nav-logo-img" />
            <span className="logo-text" style={{ color: "#000000" }}>MoonShotMap</span>
          </a>

          {/* DESKTOP NAV (Hidden on mobile via CSS) */}
          <div className="nav-links-container">
            <button
              onClick={() => handleNavClick("engine")}
              className="nav-link"
            >
              Engine
            </button>
            <button onClick={() => navigate("/blog")} className="nav-link">
              Blog
            </button>
            <button
              onClick={() => navigate("/pricing")}
              className="nav-link"
            >
              Pricing
            </button>
            <button
              onClick={() => handleNavClick("how-to")}
              className="nav-link"
              style={{
                color: currentPage === "how-to" ? "var(--text-primary)" : "",
              }}
            >
              How to Use
            </button>
            <button
              onClick={() => handleNavClick("who-for")}
              className="nav-link"
            >
              Who It’s For
            </button>
            <button
              onClick={() => handleNavClick("contact")}
              className="nav-link"
            >
              Contact
            </button>
          </div>

          <button className="login-btn" onClick={() => setShowLoginModal(true)}>
            <span>Log In</span>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>

          {/* MOBILE HAMBURGER BUTTON */}
          <button
            className="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              // Close Icon (X)
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            ) : (
              // Hamburger Icon
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            )}
          </button>
        </div>

        {/* MOBILE MENU OVERLAY */}
        <div className={`mobile-nav-overlay ${ isMobileMenuOpen ? "open" : "" }`}>
          <button
            onClick={() => handleNavClick("engine")}
            className="mobile-nav-link"
          >
            Engine
          </button>
          <button
            onClick={() => navigate("/blog")}
            className="mobile-nav-link"
          >
            Blog
          </button>
          <button
            onClick={() => navigate("/pricing")}
            className="mobile-nav-link"
          >
            Pricing
          </button>
          <button
            onClick={() => handleNavClick("how-to")}
            className="mobile-nav-link"
          >
            How to Use
          </button>
          <button
            onClick={() => handleNavClick("who-for")}
            className="mobile-nav-link"
          >
            Who It’s For
          </button>
          <button
            onClick={() => handleNavClick("contact")}
            className="mobile-nav-link"
          >
            Contact
          </button>

          <button
            className="cta-primary"
            onClick={() => {
              setIsMobileMenuOpen(false);
              setShowLoginModal(true);
            }}
            style={{ marginTop: "1rem", width: "200px" }}
          >
            Log In
          </button>
        </div>
      </nav>

      {/* RENDER CONTENT BASED ON CURRENT PAGE */}
      {renderContent()}

      {/* --- IMPROVED FOOTER --- */}
      {/* --- IMPROVED FOOTER --- */}
      <Footer />

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
    </div>
  );
};

export default LandingPage;
